import { useEffect, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { CG_TEMPLATES, boxKeys, canCarry, canRide, carrierBefore, cgTemplate, isOverlayKind, newStorylineBlock, storylineAnchors, storylineCg, type StorylineBlock, type StorylineKind } from '@airpost/shared';
import { insertAt, insertionAt, moveTo, movedIndex, sameSpot } from '../dnd.js';
import { buildTimeline, rideAtMs, ridersOf, timelineTotal } from '../storyline-timeline.js';
import { GrowingTextarea } from '../growing-textarea.js';
import { ICON, ICON_VO } from '../storyline-icons.js';
import { t } from '../i18n.js';
import { hhmmss, mmss } from '../util.js';
import { CgPreview, templateName, type CueDto } from './storyboard-panel.js';
import { StorylinePlayer } from './storyline-player.js';

/**
 * The storyline: the story as a playlist of boxes, each one a step of the piece (the presenter reads, pictures,
 * a title, a statement, the credits, a card…). The boxes are the script in another dress: every change is
 * rendered into the editor's text at once, so the autocue, the timing, the exports and the storyboard cues
 * (clips, virtual codes, CG titles, which hang on the same headings) follow without a second model.
 *
 * Everything moves by dragging: a box by its header (the body keeps its text selection), an item of the
 * menu on the left into the list at the spot the bar shows; the arrows and the click on the menu remain
 * for the keyboard and for touch screens, where the browser offers no drag.
 */

export type StorylineCueAction =
  | { action: 'choose' | 'virtual' | 'preview' | 'removeCue' | 'addCg' | 'edit'; index: number }
  | { action: 'cg'; index: number; template: string; durationMs: number | null }
  /** A title's time on the box before it (ms into it); null = a step of its own again. */
  | { action: 'at'; index: number; atMs: number | null }
  /** Any other box's second inside the box before it (kept in the story's map); null = a step of its own again. */
  | { action: 'ride'; index: number; atMs: number | null };

interface Props {
  blocks: StorylineBlock[];
  cues: CueDto[];
  /** Boxes inside boxes: box key → the second it comes in inside the box before it (titles keep theirs on their cue). */
  storylineAt: Record<string, number>;
  locale: string;
  readOnly: boolean;
  busy: boolean;
  /** The boxes after a change, with a cue action the change needs (a title dropped into a video gets its time). */
  onChange: (next: StorylineBlock[], then?: StorylineCueAction) => void;
  onCue: (a: StorylineCueAction) => void;
  /** A field that feeds a CG title lost focus: the cue follows the text. */
  onFlush: (index: number) => void;
}

/** What the menu offers: the kinds, with the video twice (with its sound, or VO: pictures the presenter speaks over). */
type MenuKey = StorylineKind | 'video-vo';
const MENU: Array<{ key: MenuKey; kind: StorylineKind; label: () => string; hint?: () => string }> = [
  { key: 'read', kind: 'read', label: () => `ON · ${t('slRead')}` },
  { key: 'video', kind: 'video', label: () => 'ΒΙΝΤΕΟ ΜΕ ΗΧΟ', hint: () => t('slSoundVideoHint') },
  { key: 'video-vo', kind: 'video', label: () => 'ΒΙΝΤΕΟ VO', hint: () => t('slVoVideoHint') },
  { key: 'title', kind: 'title', label: () => 'ΤΙΤΛΟΣ' },
  { key: 'sot', kind: 'sot', label: () => 'ΔΗΛΩΣΗ' },
  { key: 'reporter', kind: 'reporter', label: () => 'ΡΕΠΟΡΤΑΖ' },
  { key: 'card', kind: 'card', label: () => 'ΚΑΡΤΑ' },
  { key: 'natsound', kind: 'natsound', label: () => 'ΦΥΣΙΚΟΣ ΗΧΟΣ' },
  { key: 'promo', kind: 'promo', label: () => 'ALPHANEWS.LIVE' },
  { key: 'note', kind: 'note', label: () => t('slNote') },
];
/** The box a menu entry inserts. */
const makeBlock = (key: MenuKey, blocks: StorylineBlock[]): StorylineBlock => (key === 'video-vo' ? { kind: 'video', sound: false, note: '' } : newStorylineBlock(key, blocks));
const words = (s: string) => (s.match(/\S+/g) ?? []).length;
const readSeconds = (s: string) => Math.round(words(s) / 3.3);

function kindLabel(b: StorylineBlock): string {
  switch (b.kind) {
    case 'read':
      return b.mode === 'on' ? `ON · ${t('slRead')}` : `ΣΠΗΚΑΖ · ${t('slVo')}`;
    case 'video':
      return b.sound ? 'ΒΙΝΤΕΟ ΜΕ ΗΧΟ' : 'ΒΙΝΤΕΟ VO';
    case 'title':
      return b.n !== null ? `ΤΙΤΛΟΣ ${b.n}` : 'ΤΙΤΛΟΣ';
    case 'sot':
      return 'ΔΗΛΩΣΗ';
    case 'reporter':
      return 'ΡΕΠΟΡΤΑΖ';
    case 'card':
      return b.n !== null ? `ΚΑΡΤΑ ${b.n}` : 'ΚΑΡΤΑ';
    case 'natsound':
      return 'ΦΥΣΙΚΟΣ ΗΧΟΣ';
    case 'promo':
      return 'ALPHANEWS.LIVE';
    case 'note':
      return t('slNote');
  }
}

/** Seconds a box takes on air, when known: the presenter's words, the clip, the title's time. */
function boxSeconds(b: StorylineBlock, cue: CueDto | null): number | null {
  switch (b.kind) {
    case 'read':
      return readSeconds(b.text);
    case 'sot':
      return b.text.trim() ? readSeconds(b.text) : null;
    case 'video':
      return cue?.media ? Math.round(cue.media.durationMs / 1000) : null;
    case 'title':
    case 'reporter':
    case 'card':
      // the strap's time: its cue, else the template's default (what the player gives it)
      return Math.round((cue?.durationMs ?? cgTemplate(storylineCg(b)?.template)?.defaultMs ?? 7000) / 1000);
    default:
      return null;
  }
}

export function StorylinePanel({ blocks, cues, storylineAt, locale, readOnly, busy, onChange, onCue, onFlush }: Props) {
  const anchors = storylineAnchors(blocks);
  const cueFor = (i: number): CueDto | null => (anchors[i] ? (cues.find((c) => c.anchor === anchors[i]) ?? null) : null);
  const keys = boxKeys(blocks);
  /** The second a non-title box asks to come in inside the box before it. */
  const atFor = (i: number): number | null => (keys[i] ? (storylineAt[keys[i]!] ?? null) : null);
  /** Boxes inside boxes (box index → carrier index): they take no time of their own and sit under their carrier. */
  const riders = ridersOf(blocks, cueFor, atFor);
  /** Where the next box inside `carrier` comes in: after what already sits in it, else one second in; a title stays inside the box's own length. */
  const nextAtMs = (carrier: number, overlay: boolean): number => {
    let end = 1000;
    for (const [i, c] of riders) {
      if (c !== carrier) continue;
      const b = blocks[i]!;
      const cue = cueFor(i);
      const at = rideAtMs(b, cue, atFor, i) ?? 0;
      end = Math.max(end, at + (isOverlayKind(b.kind) ? (cue?.durationMs ?? 7000) : (boxSeconds(b, cue) ?? 5) * 1000));
    }
    if (!overlay) return end;
    const secs = boxSeconds(blocks[carrier]!, cueFor(carrier));
    return secs !== null && secs > 1 ? Math.min(end, (secs - 1) * 1000) : end;
  };
  /** The cue action that puts box `i` (of `kind`) at a second inside the box before it, or makes it a step of its own (null). */
  const atAction = (kind: StorylineKind, index: number, atMs: number | null): StorylineCueAction => (isOverlayKind(kind) ? { action: 'at', index, atMs } : { action: 'ride', index, atMs });
  /** The box a box sits inside, or would sit inside if given a second. */
  const carrierOf = (i: number): number | null => riders.get(i) ?? carrierBefore(blocks, i, riders);
  /** The box whose "+ inside" menu is open. */
  const [insideMenu, setInsideMenu] = useState<number | null>(null);
  /** A new box inside `carrier`, after what already sits in it. */
  const addInside = (carrier: number, key: MenuKey) => {
    const b = makeBlock(key, blocks);
    let pos = carrier + 1;
    while (pos < blocks.length && (riders.get(pos) === carrier || blocks[pos]!.kind === 'note')) pos++;
    onChange(insertAt(blocks, pos, b), atAction(b.kind, pos, nextAtMs(carrier, isOverlayKind(b.kind))));
    setActive(pos);
    setInsideMenu(null);
  };
  const set = (i: number, patch: Partial<StorylineBlock>) => onChange(blocks.map((b, k) => (k === i ? ({ ...b, ...patch } as StorylineBlock) : b)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  /** The box last touched: the menu on the left inserts after it (at the end when none). */
  const [active, setActive] = useState<number | null>(null);
  /** The box the player has on air, shown in the list and kept in view. */
  const [airing, setAiring] = useState<number | null>(null);
  useEffect(() => {
    if (airing === null) return;
    listRef.current?.querySelectorAll<HTMLElement>(':scope > .sl-box')[airing]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [airing]);
  const remove = (i: number) => {
    onChange(blocks.filter((_, k) => k !== i));
    if (active !== null && active >= i) setActive(active === i ? null : active - 1);
  };
  const add = (key: MenuKey, after: number | null = null) => {
    const b = makeBlock(key, blocks);
    const next = [...blocks];
    const at = after === null || after >= blocks.length ? blocks.length : after + 1;
    next.splice(at, 0, b);
    onChange(next);
    setActive(at);
  };
  // what the storyline lasts on air, with boxes inside boxes (one that runs past its box's end makes it longer)
  const total = timelineTotal(buildTimeline(blocks, cueFor, atFor));
  const dis = readOnly || busy;

  // ── drag and drop ──────────────────────────────────────────────────────────────
  // What is being dragged (a new box from the menu, or box `index`) and the insertion point under the pointer.
  // `at`: the insertion point between boxes; `into`: a box a title is dropped into, to ride on its pictures.
  const [drag, setDrag] = useState<{ src: { type: 'new'; key: MenuKey } | { type: 'box'; index: number }; at: number | null; into: number | null } | null>(null);
  // A box is draggable only while the pointer went down on its header, so the text in its body still selects.
  const [armed, setArmed] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (armed === null) return;
    const clear = () => setArmed(null);
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
    return () => {
      window.removeEventListener('pointerup', clear);
      window.removeEventListener('pointercancel', clear);
    };
  }, [armed]);
  const dragging = drag?.src.type === 'box' ? drag.src.index : null;
  const noop = drag !== null && drag.at !== null && dragging !== null && sameSpot(dragging, drag.at);
  const bar = drag !== null && drag.at !== null && !noop && drag.into === null ? drag.at : null;
  /** The box under the pointer a dragged box can be dropped into: one that can hold others, with the pointer well inside it. */
  const dropInto = (y: number, rects: DOMRect[]): number | null => {
    if (!drag) return null;
    const dragKind = drag.src.type === 'new' ? MENU.find((m) => m.key === (drag.src as { key: MenuKey }).key)?.kind : blocks[drag.src.index]?.kind;
    if (!dragKind || !canRide(dragKind)) return null;
    const k = rects.findIndex((r) => y >= r.top + r.height * 0.25 && y <= r.bottom - r.height * 0.25);
    if (k < 0 || !canCarry(blocks[k]!.kind) || riders.has(k) || (drag.src.type === 'box' && drag.src.index === k)) return null;
    return k;
  };
  const bounds = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>(':scope > .sl-box') ?? [], (el) => el.getBoundingClientRect());
  const armBox = (i: number) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (readOnly || (e.target as HTMLElement).closest('input, button, select, textarea, label, a')) return;
    setArmed(i);
  };
  const startBox = (i: number) => (e: ReactDragEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // a text selection dragged out of a body: not ours
    if (readOnly || armed !== i) return e.preventDefault();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${t('slBoxes')} ${i + 1}`);
    const head = e.currentTarget.querySelector<HTMLElement>(':scope > .sl-head');
    if (head) e.dataTransfer.setDragImage(head, 24, 16);
    setDrag({ src: { type: 'box', index: i }, at: null, into: null });
  };
  const startNew = (key: MenuKey) => (e: ReactDragEvent<HTMLDivElement>) => {
    if (dis) return e.preventDefault();
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', key);
    setDrag({ src: { type: 'new', key }, at: null, into: null });
  };
  const endDrag = () => {
    setDrag(null);
    setArmed(null);
  };
  // Both dragenter and dragover are cancelled: Chrome decides from the last one it fired whether a drop is accepted,
  // and a quick release right after entering a box sees only the dragenter.
  const overList = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!drag) return; // files or text from elsewhere keep the browser's own behaviour
    e.preventDefault();
    e.dataTransfer.dropEffect = drag.src.type === 'new' ? 'copy' : 'move';
    const rects = bounds();
    const at = insertionAt(e.clientY, rects);
    const into = dropInto(e.clientY, rects);
    setDrag((d) => (d && (d.at !== at || d.into !== into) ? { ...d, at, into } : d));
  };
  const leaveList = (e: ReactDragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDrag((d) => (d && (d.at !== null || d.into !== null) ? { ...d, at: null, into: null } : d));
  };
  const dropOnList = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!drag) return;
    e.preventDefault();
    if (drag.into !== null) {
      // into a box: the box goes after it and what already sits in it, and gets its second
      const carrier = drag.into;
      let pos = carrier + 1;
      while (pos < blocks.length && (riders.get(pos) === carrier || blocks[pos]!.kind === 'note')) pos++;
      if (drag.src.type === 'new') {
        const b = makeBlock(drag.src.key, blocks);
        onChange(insertAt(blocks, pos, b), atAction(b.kind, pos, nextAtMs(carrier, isOverlayKind(b.kind))));
        setActive(pos);
      } else {
        const from = drag.src.index;
        const kind = blocks[from]!.kind;
        const idx = movedIndex(from, pos);
        onChange(moveTo(blocks, from, pos), atAction(kind, idx, nextAtMs(carrier, isOverlayKind(kind))));
        setActive(idx);
      }
      endDrag();
      return;
    }
    const at = drag.at ?? insertionAt(e.clientY, bounds());
    if (drag.src.type === 'new') {
      onChange(insertAt(blocks, at, makeBlock(drag.src.key, blocks)));
      setActive(at);
    } else if (!sameSpot(drag.src.index, at)) {
      // dropped between boxes: a box that sat inside another becomes a step of its own there
      const from = drag.src.index;
      const idx = movedIndex(from, at);
      onChange(moveTo(blocks, from, at), riders.has(from) ? atAction(blocks[from]!.kind, idx, null) : undefined);
      setActive(idx);
    }
    endDrag();
  };

  const videoCue = (i: number) => {
    const cue = cueFor(i);
    if (cue?.mediaAssetId) {
      return (
        <div className="sl-cue set">
          <img className="cue-thumb" alt="" loading="lazy" src={`/api/media/${encodeURIComponent(cue.mediaAssetId)}/thumb`} onError={(e) => (e.currentTarget.style.display = 'none')} />
          <b className="cue-title">{cue.media?.title ?? cue.mediaAssetId}</b>
          {cue.media && <span className="cue-dur">{hhmmss(Math.round(cue.media.durationMs / 1000))}</span>}
          {cue.media && <span className={`status ${cue.media.status === 'READY' ? 'ok' : 'warn'}`}>{cue.media.status}</span>}
          {cue.mediaCode && <span className="muted">({cue.mediaCode})</span>}
          <span className="spacer" />
          {cue.edit && <span className="status info" title={cue.edit.title}>✂ {cue.edit.rendered ? t('slEditRendered') : t('slEditDraft')}</span>}
          <button className="btn small" type="button" onClick={() => onCue({ action: 'preview', index: i })}>
            ▶ {t('cuePreview')}
          </button>
          <button className="btn small" type="button" disabled={readOnly && !cue.edit} onClick={() => onCue({ action: 'edit', index: i })} title={t('slEditHint')}>
            ✂ {cue.edit ? t('slEditOpen') : t('slEdit')}
          </button>
          <button className="btn small" type="button" disabled={dis} onClick={() => onCue({ action: 'choose', index: i })} title={t('storyVideoChoose')}>
            ✎
          </button>
          <button className="btn small danger" type="button" disabled={dis} onClick={() => onCue({ action: 'removeCue', index: i })} title={t('storyVideoRemove')}>
            ✖
          </button>
        </div>
      );
    }
    if (cue?.mediaCode) {
      return (
        <div className="sl-cue set">
          <span className="cue-code">⌛ {cue.mediaCode}</span>
          <span className="muted">{t('virtualWaiting')}</span>
          <span className="spacer" />
          <button className="btn small" type="button" disabled={dis} onClick={() => onCue({ action: 'choose', index: i })}>
            {t('storyVideoChoose')}
          </button>
          <button className="btn small danger" type="button" disabled={dis} onClick={() => onCue({ action: 'removeCue', index: i })} title={t('storyVideoRemove')}>
            ✖
          </button>
        </div>
      );
    }
    return (
      <div className="sl-cue">
        <span className="muted">{t('slNoClip')}</span>
        <span className="spacer" />
        <button className="btn small" type="button" disabled={dis} onClick={() => onCue({ action: 'choose', index: i })}>
          ＋ {t('storyVideoChoose')}
        </button>
        <button className="btn small" type="button" disabled={dis} onClick={() => onCue({ action: 'virtual', index: i })} title={t('storyVideoVirtualHint')}>
          ⌛ {t('storyVideoVirtual')}
        </button>
      </div>
    );
  };

  const cgCue = (i: number, b: StorylineBlock) => {
    const cg = storylineCg(b);
    if (!cg) return null;
    const cue = cueFor(i);
    return (
      <div className={`sl-cue${cue ? ' set cg' : ''}`}>
          {cue ? (
            <>
              <span className="cue-cg">CG</span>
              <select value={cue.template ?? cg.template} disabled={dis} onChange={(e) => onCue({ action: 'cg', index: i, template: e.target.value, durationMs: cgTemplate(e.target.value)?.defaultMs ?? cue.durationMs })} title={t('cueTemplate')}>
                {CG_TEMPLATES.map((x) => (
                  <option key={x.key} value={x.key}>
                    {locale === 'en' ? x.en : x.el}
                  </option>
                ))}
              </select>
              <input className="dur" type="number" min={0} max={3600} value={Math.round((cue.durationMs ?? 0) / 1000)} disabled={dis} onChange={(e) => onCue({ action: 'cg', index: i, template: cue.template ?? cg.template, durationMs: Math.max(0, Number(e.target.value) || 0) * 1000 })} title={t('cueDurationS')} />
              <span className="muted">″</span>
              <span className="spacer" />
              <button className="btn small danger" type="button" disabled={dis} onClick={() => onCue({ action: 'removeCue', index: i })} title={t('slRemoveCg')}>
                ✖ CG
              </button>
            </>
          ) : (
            <>
              <span className="muted">{templateName(cg.template, locale)}</span>
              <span className="spacer" />
              <button className="btn small" type="button" disabled={dis} onClick={() => onCue({ action: 'addCg', index: i })}>
                ＋ {t('slAddCg')}
              </button>
            </>
          )}
      </div>
    );
  };

  /** A box with a title on the picture: its fields and the CG row on the left, the preview beside them (under them it doubled the box). */
  const withCg = (i: number, b: StorylineBlock, fields: ReactNode) => {
    const cg = storylineCg(b);
    if (!cg) return fields;
    const cue = cueFor(i);
    return (
      <div className="sl-cg">
        <div className="sl-cg-fields">
          {fields}
          {cgCue(i, b)}
        </div>
        <div className="sl-cg-preview">
          <CgPreview template={cue?.template ?? cg.template} lines={cg.lines} />
        </div>
      </div>
    );
  };

  /** A box's place: inside the box before it at a second (then a time field), or a step of its own (then a button to put it in). */
  const atControl = (i: number) => {
    const b = blocks[i]!;
    const carrier = carrierOf(i);
    if (carrier === null) return null;
    const atMs = riders.has(i) ? rideAtMs(b, cueFor(i), atFor, i) : null;
    const carrierBox = blocks[carrier]!;
    const overlay = isOverlayKind(b.kind);
    if (atMs === null)
      return (
        <button className="btn small" type="button" disabled={dis} onClick={() => onCue(atAction(b.kind, i, nextAtMs(carrier, overlay)))} title={t('slAttachHint')}>
          ⤴ {t('slInside')} {kindLabel(carrierBox)}
        </button>
      );
    return (
      <span className="sl-at" title={t('slAtHint')}>
        ⤷ {t('slInside')} {carrier + 1} · {t('slAt')}
        <input type="number" min={0} max={3600} step={1} value={Math.round(atMs / 1000)} disabled={dis} onChange={(e) => onCue(atAction(b.kind, i, Math.max(0, Number(e.target.value) || 0) * 1000))} />
        ″
        <button className="btn small" type="button" disabled={dis} onClick={() => onCue(atAction(b.kind, i, null))} title={t('slDetachHint')}>
          {t('slDetach')}
        </button>
      </span>
    );
  };

  /** "+ inside": a menu of the kinds to put inside box `i`, at the next free second. */
  const insideControl = (i: number) => (
    <span className="sl-inside">
      <button className="btn small" type="button" disabled={dis} onClick={() => setInsideMenu(insideMenu === i ? null : i)} title={t('slInsideAddHint')} aria-expanded={insideMenu === i}>
        ＋ {t('slInsideAdd')}
      </button>
      {insideMenu === i && (
        <span className="sl-inside-menu" role="menu" onMouseLeave={() => setInsideMenu(null)}>
          {MENU.filter((m) => canRide(m.kind)).map((m) => (
            <button key={m.key} type="button" role="menuitem" className={`sl-inside-item ${m.kind}${m.key === 'video-vo' ? ' vo' : ''}`} onClick={() => addInside(i, m.key)}>
              <span className="icon">{m.key === 'video-vo' ? ICON_VO : ICON[m.kind]}</span> {m.label()}
            </button>
          ))}
        </span>
      )}
    </span>
  );

  const linesEditor = (i: number, lines: string[], max: number, ph: string) => (
    <div className="sl-lines">
      {lines.map((l, k) => (
        <div key={k} className="sl-line">
          <input type="text" value={l} maxLength={200} disabled={readOnly} placeholder={`${ph} ${k + 1}`} onChange={(e) => set(i, { lines: lines.map((x, m) => (m === k ? e.target.value : x)) } as Partial<StorylineBlock>)} onBlur={() => onFlush(i)} />
          {k === lines.length - 1 && lines.length < max && (
            <button className="btn small" type="button" disabled={readOnly} onClick={() => set(i, { lines: [...lines, ''] } as Partial<StorylineBlock>)}>
              {t('slAddLine')}
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const insertPoint = active !== null && active < blocks.length ? active : null;
  return (
    <div className="sl">
      <p className="tip">{t('slHint')}</p>
      <div className={`sl-cols${readOnly ? ' no-menu' : ''}`}>
        {!readOnly && (
          <aside className="sl-menu">
            <div className="sl-menu-head">{t('slAdd')}</div>
            {MENU.map((a) => (
              <div
                key={a.key}
                className={`sl-menu-item ${a.kind}${a.key === 'video-vo' ? ' vo' : ''}${busy ? ' disabled' : ''}`}
                role="button"
                tabIndex={busy ? -1 : 0}
                aria-disabled={busy}
                draggable={!busy}
                onDragStart={startNew(a.key)}
                onDragEnd={endDrag}
                onClick={() => !busy && add(a.key, insertPoint)}
                onKeyDown={(e) => {
                  if (busy || (e.key !== 'Enter' && e.key !== ' ')) return;
                  e.preventDefault();
                  add(a.key, insertPoint);
                }}
                title={`${a.hint ? `${a.hint()} · ` : ''}${insertPoint !== null ? `${t('slAfterBox')} ${insertPoint + 1}` : t('slAtEnd')} · ${t('slDragMenu')}`}
              >
                <span className="icon">{a.key === 'video-vo' ? ICON_VO : ICON[a.kind]}</span>
                <span>{a.label()}</span>
              </div>
            ))}
            <div className="sl-menu-foot muted">
              <div>{insertPoint !== null ? `${t('slAfterBox')} ${insertPoint + 1}` : t('slAtEnd')}</div>
              <div>{t('slDragMenu')}</div>
            </div>
          </aside>
        )}
        <div className="sl-main" onDragEnter={overList} onDragOver={overList} onDragLeave={leaveList} onDrop={dropOnList}>
          <div ref={listRef} className={`sl-list${bar !== null && blocks.length === 0 ? ' drop-target' : ''}`}>
            {blocks.length === 0 && <p className="muted">{drag ? t('slDropHere') : t('slEmpty')}</p>}
            {blocks.map((b, i) => {
              const secs = riders.has(i) ? null : boxSeconds(b, cueFor(i));
              return (
                <div
                  key={i}
                  className={`sl-box ${b.kind}${b.kind === 'read' ? ` ${b.mode}` : b.kind === 'video' && !b.sound ? ' vo' : ''}${active === i ? ' active' : ''}${airing === i ? ' airing' : ''}${riders.has(i) ? ' on-carrier' : ''}${drag?.into === i ? ' drop-into' : ''}${dragging === i ? ' dragging' : ''}${bar === i ? ' drop-before' : ''}${bar === blocks.length && i === blocks.length - 1 ? ' drop-after' : ''}`}
                  draggable={!readOnly && (armed === i || dragging === i)}
                  onDragStart={startBox(i)}
                  onDragEnd={endDrag}
                  onFocusCapture={() => setActive(i)}
                  onClickCapture={() => setActive(i)}
                >
                  <div className={`sl-head${readOnly ? '' : ' grab'}`} onPointerDown={armBox(i)}>
                    {!readOnly && (
                      <span className="grip" title={t('slDragBox')} aria-hidden="true">
                        ⠿
                      </span>
                    )}
                    <span className="num">{i + 1}</span>
                    <span className="icon">{b.kind === 'video' && !b.sound ? ICON_VO : ICON[b.kind]}</span>
                    <span className="kind" title={b.kind === 'video' ? (b.sound ? t('slSoundVideoHint') : t('slVoVideoHint')) : undefined}>
                      {kindLabel(b)}
                    </span>
                    {b.kind === 'read' && (
                      <span className="sl-seg">
                        <button type="button" className={b.mode === 'on' ? 'on' : ''} disabled={readOnly} onClick={() => set(i, { mode: 'on' })} title={t('slModeOnHint')}>
                          ON
                        </button>
                        <button type="button" className={b.mode === 'vo' ? 'on' : ''} disabled={readOnly} onClick={() => set(i, { mode: 'vo' })} title={t('slModeVoHint')}>
                          ΣΠΗΚΑΖ
                        </button>
                      </span>
                    )}
                    {(b.kind === 'title' || b.kind === 'card') && (
                      <label className="sl-n">
                        {t('slNumber')} <input type="number" min={1} max={99} value={b.n ?? ''} disabled={readOnly} onChange={(e) => set(i, { n: e.target.value === '' ? null : Math.max(1, Number(e.target.value) || 1) })} />
                      </label>
                    )}
                    {canRide(b.kind) && atControl(i)}
                    {!readOnly && canCarry(b.kind) && !riders.has(i) && insideControl(i)}
                    <span className="dur">{secs !== null ? mmss(secs) : ''}</span>
                    <span className="tools">
                      <button type="button" disabled={readOnly || i === 0} onClick={() => move(i, -1)} title={t('moveUp')}>
                        ▲
                      </button>
                      <button type="button" disabled={readOnly || i === blocks.length - 1} onClick={() => move(i, 1)} title={t('moveDown')}>
                        ▼
                      </button>
                      <button type="button" disabled={readOnly} onClick={() => remove(i)} title={t('delete')}>
                        ✖
                      </button>
                    </span>
                  </div>
                  <div className="sl-body">
                    {b.kind === 'read' && <GrowingTextarea value={b.text} rows={2} disabled={readOnly} placeholder={b.mode === 'on' ? t('slReadPh') : t('slVoPh')} spellCheck lang="el" onChange={(e) => set(i, { text: e.target.value })} />}
                    {b.kind === 'video' && (
                      <>
                        {videoCue(i)}
                        <input type="text" value={b.note} maxLength={300} disabled={readOnly} placeholder={t('slNotePh')} onChange={(e) => set(i, { note: e.target.value })} />
                      </>
                    )}
                    {b.kind === 'title' && withCg(i, b, linesEditor(i, b.lines.length ? b.lines : [''], 2, t('cueLine')))}
                    {b.kind === 'sot' &&
                      withCg(
                        i,
                        b,
                        <>
                          <div className="sl-row">
                          <input type="text" value={b.name} maxLength={120} disabled={readOnly} placeholder={t('slName')} onChange={(e) => set(i, { name: e.target.value })} onBlur={() => onFlush(i)} style={{ flex: '1 1 220px' }} />
                          <input type="text" value={b.role} maxLength={120} disabled={readOnly} placeholder={t('slRole')} onChange={(e) => set(i, { role: e.target.value })} onBlur={() => onFlush(i)} style={{ flex: '1 1 220px' }} />
                        </div>
                          <GrowingTextarea value={b.text} rows={2} disabled={readOnly} placeholder={t('slQuotePh')} spellCheck lang="el" onChange={(e) => set(i, { text: e.target.value })} />
                        </>,
                      )}
                    {b.kind === 'reporter' &&
                      withCg(
                        i,
                        b,
                        <div className="sl-row">
                          <input type="text" value={b.name} maxLength={120} disabled={readOnly} placeholder={t('slReporterName')} onChange={(e) => set(i, { name: e.target.value })} onBlur={() => onFlush(i)} style={{ flex: '1 1 200px' }} />
                          <input type="text" value={b.email} maxLength={120} disabled={readOnly} placeholder="email" onChange={(e) => set(i, { email: e.target.value })} style={{ flex: '1 1 200px' }} />
                          <input type="text" value={b.camera} maxLength={120} disabled={readOnly} placeholder={t('slCamera')} onChange={(e) => set(i, { camera: e.target.value })} onBlur={() => onFlush(i)} style={{ flex: '1 1 200px' }} />
                        </div>,
                      )}
                    {b.kind === 'card' && withCg(i, b, linesEditor(i, b.lines.length ? b.lines : [''], 6, t('cueLine')))}
                    {b.kind === 'promo' && linesEditor(i, b.lines.length ? b.lines : [''], 4, t('cueLine'))}
                    {b.kind === 'natsound' && <span className="muted">{t('slNatsoundHint')}</span>}
                    {b.kind === 'note' && <GrowingTextarea value={b.text} rows={2} disabled={readOnly} placeholder={t('slNoteBoxPh')} onChange={(e) => set(i, { text: e.target.value })} />}
                  </div>
                  {!readOnly && (
                    <div className="sl-insert">
                      {MENU.map((a) => (
                        <button key={a.key} type="button" onClick={() => add(a.key, i)} title={`${t('slInsertAfter')}: ${a.label()}`}>
                          {a.key === 'video-vo' ? ICON_VO : ICON[a.kind]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="sl-total">
            <span>
              {blocks.length} {t('slBoxes')}
            </span>
            <span>
              {t('slTotal')} ≈ {mmss(total)}
            </span>
          </div>
        </div>
        <aside className="sl-player">
          <StorylinePlayer blocks={blocks} cues={blocks.map((_, i) => cueFor(i))} atFor={atFor} onBox={setAiring} />
        </aside>
      </div>
    </div>
  );
}
